import React, { useContext, useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../components/Layout/DashboardLayout";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import CreditsPurchaseModal from "../../components/Credits/CreditsPurchaseModal";
import { AuthContext } from "../../context/AuthContext";
import * as creditService from "../../services/creditService";
import paymentService from "../../services/paymentService";
import { formatDate } from "../../utils/formatters";

export default function Credits() {
  const { credits, updateCredits, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all");
  const [openPurchase, setOpenPurchase] = useState(false);
  const [lastReset, setLastReset] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const bal = await creditService.getBalance();
        if (mounted)
          setLastReset(
            bal.dailyCreditsResetDate || user?.dailyCreditsResetDate || null
          );
        await updateCredits();
        const h = await creditService.getHistory(page, limit);
        if (mounted) {
          setHistory(h.transactions || []);
          setTotal(h.total || 0);
        }
      } catch {
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const result = await paymentService.getPaymentHistory();
        if (mounted) setPaymentHistory(result.transactions || []);
      } catch {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return history;
    return history.filter((t) => t.type === filter);
  }, [filter, history]);

  const computedBalances = useMemo(() => {
    // Only compute for first page; for others we don't have base
    if (page !== 1) return [];
    const balances = [];
    let current = credits;
    for (let i = 0; i < filtered.length; i += 1) {
      balances.push(current);
      current -= filtered[i].amount || 0;
    }
    return balances;
  }, [filtered, credits, page]);

  const nextResetText = useMemo(() => {
    if (!lastReset) return "";
    const next = new Date(new Date(lastReset).getTime() + 24 * 60 * 60 * 1000);
    const diffMs = next.getTime() - Date.now();
    if (diffMs <= 0) return "Resets soon";
    const hours = Math.floor(diffMs / 3600000);
    const mins = Math.floor((diffMs % 3600000) / 60000);
    return `${hours}h ${mins}m`;
  }, [lastReset]);

  if (loading) {
    return (
      <DashboardLayout>
        <LoadingSpinner fullScreen />
      </DashboardLayout>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Credits</h1>
            <p className="text-gray-600">
              Current balance: <span className="font-semibold">{credits}</span>
            </p>
            {credits < 5 && nextResetText && (
              <p className="text-sm text-gray-500">
                Daily reset in {nextResetText}
              </p>
            )}
          </div>
          <Button onClick={() => setOpenPurchase(true)}>Buy Credits</Button>
        </div>

        {/* Purchase section */}
        <Card title="Purchase Credits">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <PricingCard
              title="Small"
              price={10}
              credits={20}
              onSelect={() => setOpenPurchase(true)}
            />
            <PricingCard
              title="Medium"
              price={25}
              credits={50}
              tag="Most Popular"
              onSelect={() => setOpenPurchase(true)}
            />
            <PricingCard
              title="Large"
              price={50}
              credits={120}
              tag="Best Value"
              onSelect={() => setOpenPurchase(true)}
            />
          </div>
        </Card>

        {/* Payment History (Purchases only) */}
        <Card title="Payment History (Purchases)">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Description</th>
                  <th className="text-left px-4 py-2">Credits</th>
                  <th className="text-left px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {!paymentHistory.length ? (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      No purchases yet.
                    </td>
                  </tr>
                ) : (
                  paymentHistory.map((t) => (
                    <tr key={t._id} className="border-t">
                      <td className="px-4 py-2">
                        {formatDate(t.transactionDate)}
                      </td>
                      <td className="px-4 py-2">{t.description}</td>
                      <td className="px-4 py-2 text-green-600">+{t.amount}</td>
                      <td className="px-4 py-2">
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                          Success
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* History */}
        <Card title="Transaction History">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-3">
            <select
              className="border rounded px-2 py-1 text-sm w-full sm:w-auto"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="purchase">Purchase</option>
              <option value="usage">Usage</option>
              <option value="referral">Referral</option>
              <option value="daily_reset">Daily Reset</option>
            </select>
            <div className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2">Date & Time</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-left px-4 py-2">Description</th>
                  <th className="text-left px-4 py-2">Amount</th>
                  <th className="text-left px-4 py-2">Balance After</th>
                </tr>
              </thead>
              <tbody>
                {!filtered.length ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      No transactions found.
                    </td>
                  </tr>
                ) : (
                  filtered.map((t, idx) => (
                    <tr key={t._id || idx} className="border-t">
                      <td className="px-4 py-2">
                        {new Date(t.transactionDate).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 capitalize">
                        {t.type.replace("_", " ")}
                      </td>
                      <td className="px-4 py-2">{t.description}</td>
                      <td
                        className={`px-4 py-2 ${
                          t.amount < 0 ? "text-red-600" : "text-green-600"
                        }`}
                      >
                        {t.amount > 0 ? "+" : ""}
                        {t.amount}
                      </td>
                      <td className="px-4 py-2">
                        {page === 1 ? computedBalances[idx] : "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-end gap-2 mt-3">
            <Button
              variant="ghost"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="ghost"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </Card>

        {/* Daily info */}
        <Card title="Daily Credits">
          <p className="text-sm text-gray-600">
            You receive up to 5 credits daily. Unused credits carry over.
          </p>
          {lastReset && (
            <div className="mt-3">
              <div className="h-2 bg-gray-200 rounded">
                <div
                  className="h-2 bg-primary-600 rounded"
                  style={{ width: progressPercent(lastReset) }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Next reset in {nextResetText}
              </p>
            </div>
          )}
        </Card>
      </div>
      <CreditsPurchaseModal
        isOpen={openPurchase}
        onClose={() => setOpenPurchase(false)}
        onPurchased={async () => {
          await updateCredits();
          setPage(1);
        }}
      />
    </DashboardLayout>
  );
}

function PricingCard({ title, price, credits, tag, onSelect }) {
  return (
    <div className="bg-white rounded-lg shadow border border-gray-100 p-6 relative">
      {tag && (
        <div className="absolute -top-2 right-2 bg-primary-600 text-white text-xs px-2 py-0.5 rounded">
          {tag}
        </div>
      )}
      <h3 className="text-lg font-semibold">{title}</h3>
      <div className="mt-2 text-4xl font-bold">${price}</div>
      <p className="text-sm text-gray-600">{credits} credits</p>
      <button
        className="mt-4 px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700"
        onClick={onSelect}
      >
        Purchase
      </button>
    </div>
  );
}

function progressPercent(lastReset) {
  const start = new Date(lastReset).getTime();
  const now = Date.now();
  const end = start + 24 * 60 * 60 * 1000;
  const pct = Math.min(1, Math.max(0, (now - start) / (end - start)));
  return `${Math.round(pct * 100)}%`;
}
